import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { httpsCallable } from 'firebase/functions'
import { functions } from '@/lib/firebase'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Search, Eye, Calendar, User, RefreshCw, CheckSquare, Merge, X, UserPlus, ChevronLeft, ChevronRight, Archive, Trash2 } from 'lucide-react'
import { formatRelativeTime, parseTimestamp } from '@/lib/dateTime'
import { PersonDetailDialog } from '@/components/PersonDetailDialog'
import { AddPersonDialog } from '@/components/AddPersonDialog'
import type { Person, Classification } from '@/types'

interface PersonsCatalogProps {
  groupId: string
  onNavigateToSightings?: (personId: string, sightingId?: string, personName?: string) => void
  canManagePersons?: boolean
  urlPersonId?: string | null
  isAdmin?: boolean
}

const PAGE_SIZE = 20

export function PersonsCatalog({ groupId, onNavigateToSightings, canManagePersons = true, urlPersonId, isAdmin = false }: PersonsCatalogProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const currentPage = Math.max(1, parseInt(searchParams.get('page') || '1', 10))
  const cursorFromUrl = searchParams.get('cursor') || ''
  const selectedClassification = searchParams.get('classification') || 'all'
  const showArchived = searchParams.get('archived') === 'true'
  const searchFromUrl = searchParams.get('search') || ''
  // Search/archive paths use legacy page-based pagination (totalCount is available there
  // because those queries already scan all matching docs). Normal mode uses cursors to
  // avoid full-collection scans.
  const isLegacyPageMode = !!searchFromUrl || showArchived
  const [persons, setPersons] = useState<Person[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  // In-memory stack of cursors used so the user can navigate back. The empty-string
  // entry represents the first page (no cursor). Stack does NOT survive a reload.
  const [cursorStack, setCursorStack] = useState<string[]>([])
  const [classifications, setClassifications] = useState<Classification[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState(searchFromUrl)
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Set to true when a multi-page jump has already populated state for the upcoming
  // cursor URL change, so the URL-driven useEffect should skip one fetch.
  const skipNextLoadRef = useRef(false)

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE))
  const cursorPageIndex = cursorStack.length + 1
  const canGoPrev = isLegacyPageMode ? currentPage > 1 : cursorStack.length > 0
  const canGoNext = isLegacyPageMode ? currentPage < totalPages : hasMore

  // Selection mode for multi-select merge/archive/delete
  const [selectionMode, setSelectionMode] = useState(false)
  const [selectedPersonIds, setSelectedPersonIds] = useState<Set<string>>(new Set())
  const [mergeConfirmOpen, setMergeConfirmOpen] = useState(false)
  const [merging, setMerging] = useState(false)
  const [mergeError, setMergeError] = useState<string | null>(null)
  const [bulkArchiveConfirmOpen, setBulkArchiveConfirmOpen] = useState(false)
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState(false)
  const [bulkProcessing, setBulkProcessing] = useState(false)

  // Add person dialog
  const [addPersonOpen, setAddPersonOpen] = useState(false)

  // Person detail dialog - driven by URL
  const [selectedPerson, setSelectedPerson] = useState<Person | null>(null)
  const detailDialogOpen = !!urlPersonId
  const selectedPersonId = urlPersonId || null

  const loadClassifications = useCallback(async () => {
    if (!groupId) return

    try {
      const getClassificationsFn = httpsCallable(functions, 'getClassifications')
      const result = await getClassificationsFn({ groupId })
      const data = result.data as { success: boolean; classifications: Classification[] }

      if (data.success) {
        setClassifications(data.classifications)
      }
    } catch (err) {
      console.error('Failed to load classifications:', err)
    }
  }, [groupId])

  // Debounce search input by 400ms and sync to URL
  useEffect(() => {
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current)
    }
    searchTimerRef.current = setTimeout(() => {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev)
        if (searchQuery) {
          next.set('search', searchQuery)
        } else {
          next.delete('search')
        }
        next.delete('page')
        next.delete('cursor')
        return next
      })
      setCursorStack([])
    }, 400)
    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current)
      }
    }
  }, [searchQuery])

  const loadPersons = useCallback(async () => {
    if (!groupId) return

    setLoading(true)
    setError(null)

    try {
      const getPersonsFn = httpsCallable(functions, 'getPersons')
      const params: Record<string, unknown> = {
        groupId,
        classificationId: selectedClassification !== 'all' ? selectedClassification : undefined,
        search: searchFromUrl || undefined,
        pageSize: PAGE_SIZE,
        onlyArchivedExpired: showArchived || undefined,
      }
      if (isLegacyPageMode) {
        params.page = currentPage
      } else if (cursorFromUrl) {
        params.cursor = cursorFromUrl
      }
      const result = await getPersonsFn(params)
      const data = result.data as {
        success: boolean
        persons: Person[]
        totalCount?: number
        page?: number
        pageSize: number
        nextCursor?: string | null
        hasMore?: boolean
        skippedCursors?: string[]
      }

      if (data.success) {
        setPersons(data.persons)
        if (isLegacyPageMode) {
          setTotalCount(data.totalCount || 0)
          setHasMore(false)
          setNextCursor(null)
        } else {
          setTotalCount(0)
          setHasMore(!!data.hasMore)
          setNextCursor(data.nextCursor || null)
        }
      } else {
        setError(t('persons.failedToLoad'))
      }
    } catch (err) {
      console.error('Failed to load persons:', err)
      setError(t('persons.failedToLoad'))
    } finally {
      setLoading(false)
    }
  }, [groupId, selectedClassification, searchFromUrl, currentPage, cursorFromUrl, isLegacyPageMode, showArchived])

  useEffect(() => {
    loadClassifications()
  }, [loadClassifications])

  useEffect(() => {
    if (skipNextLoadRef.current) {
      skipNextLoadRef.current = false
      return
    }
    loadPersons()
  }, [loadPersons])

  const setPage = useCallback((newPage: number) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      if (newPage <= 1) {
        next.delete('page')
      } else {
        next.set('page', String(newPage))
      }
      return next
    })
  }, [setSearchParams])

  const goNext = useCallback(() => {
    if (isLegacyPageMode) {
      setPage(currentPage + 1)
      return
    }
    if (!hasMore || !nextCursor) return
    setCursorStack(prev => [...prev, cursorFromUrl])
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      next.set('cursor', nextCursor)
      return next
    })
  }, [isLegacyPageMode, currentPage, setPage, hasMore, nextCursor, cursorFromUrl, setSearchParams])

  const goPrev = useCallback(() => {
    if (isLegacyPageMode) {
      setPage(Math.max(1, currentPage - 1))
      return
    }
    if (cursorStack.length === 0) return
    const prevCursor = cursorStack[cursorStack.length - 1]
    setCursorStack(prev => prev.slice(0, -1))
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      if (prevCursor) {
        next.set('cursor', prevCursor)
      } else {
        next.delete('cursor')
      }
      return next
    })
  }, [isLegacyPageMode, currentPage, setPage, cursorStack, setSearchParams])

  // Jump back k pages by popping k entries from the cursor stack. stack[i] holds the
  // cursor used to reach page i+1, so the oldest popped entry IS the cursor for the target page.
  const goBackPages = useCallback((k: number) => {
    if (k <= 0 || cursorStack.length === 0) return
    const popCount = Math.min(k, cursorStack.length)
    const targetCursor = cursorStack[cursorStack.length - popCount]
    setCursorStack(prev => prev.slice(0, -popCount))
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      if (targetCursor) {
        next.set('cursor', targetCursor)
      } else {
        next.delete('cursor')
      }
      return next
    })
  }, [cursorStack, setSearchParams])

  // Jump forward k pages (1–4). Single request reads ~(k+1)*pageSize docs, returns the
  // target page plus boundary cursors for skipped pages so Prev still walks one page at a time.
  const goForwardPages = useCallback(async (k: number) => {
    if (k <= 0 || isLegacyPageMode || !hasMore) return
    try {
      setLoading(true)
      setError(null)
      const getPersonsFn = httpsCallable(functions, 'getPersons')
      const params: Record<string, unknown> = {
        groupId,
        classificationId: selectedClassification !== 'all' ? selectedClassification : undefined,
        pageSize: PAGE_SIZE,
        skipPages: k,
      }
      if (cursorFromUrl) params.cursor = cursorFromUrl
      const result = await getPersonsFn(params)
      const data = result.data as {
        success: boolean
        persons: Person[]
        nextCursor?: string | null
        hasMore?: boolean
        skippedCursors?: string[]
      }
      if (!data.success) {
        setError(t('persons.failedToLoad'))
        return
      }
      const cursors = data.skippedCursors || []
      if (cursors.length === 0) {
        // Jump yielded no data — stay on current page.
        return
      }
      // Push current cursor + all returned boundary cursors except the last (that
      // last one is the cursor used to reach the target page, which belongs in the URL).
      const toPush = [cursorFromUrl, ...cursors.slice(0, -1)]
      setPersons(data.persons)
      setTotalCount(0)
      setHasMore(!!data.hasMore)
      setNextCursor(data.nextCursor || null)
      setCursorStack(prev => [...prev, ...toPush])
      skipNextLoadRef.current = true
      setSearchParams(prev => {
        const next = new URLSearchParams(prev)
        next.set('cursor', cursors[cursors.length - 1])
        return next
      })
    } catch (err) {
      console.error('Failed to jump ahead:', err)
      setError(t('persons.failedToLoad'))
    } finally {
      setLoading(false)
    }
  }, [isLegacyPageMode, hasMore, groupId, selectedClassification, cursorFromUrl, setSearchParams, t])

  const setSelectedClassification = useCallback((value: string) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      if (value === 'all') {
        next.delete('classification')
      } else {
        next.set('classification', value)
      }
      next.delete('page')
      next.delete('cursor')
      return next
    })
    setCursorStack([])
  }, [setSearchParams])

  const setShowArchived = useCallback((value: boolean) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      if (value) {
        next.set('archived', 'true')
      } else {
        next.delete('archived')
      }
      next.delete('page')
      next.delete('cursor')
      return next
    })
    setCursorStack([])
  }, [setSearchParams])

  const togglePersonSelection = (personId: string) => {
    setSelectedPersonIds(prev => {
      const next = new Set(prev)
      if (next.has(personId)) {
        next.delete(personId)
      } else {
        next.add(personId)
      }
      return next
    })
  }

  const exitSelectionMode = () => {
    setSelectionMode(false)
    setSelectedPersonIds(new Set())
    setMergeError(null)
  }

  const handleMergeSelected = async () => {
    if (selectedPersonIds.size < 2) return

    setMerging(true)
    setMergeError(null)

    try {
      // Find the oldest person (by createdAt) among selected - this one keeps its metadata
      const selectedPersons = persons.filter(p => selectedPersonIds.has(p.id))

      // Debug: log raw timestamp values before sorting
      console.log('[Merge] Selected persons before sort:', selectedPersons.map(p => ({
        id: p.id,
        name: p.name,
        createdAt_raw: p.createdAt,
        firstSeenAt_raw: p.firstSeenAt,
        createdAt_parsed: parseTimestamp(p.createdAt)?.toISOString() ?? null,
        firstSeenAt_parsed: parseTimestamp(p.firstSeenAt)?.toISOString() ?? null,
        sort_value: parseTimestamp(p.createdAt)?.getTime() ?? parseTimestamp(p.firstSeenAt)?.getTime() ?? Infinity,
      })))

      selectedPersons.sort((a, b) => {
        const aTime = parseTimestamp(a.createdAt)?.getTime() ?? parseTimestamp(a.firstSeenAt)?.getTime() ?? Infinity
        const bTime = parseTimestamp(b.createdAt)?.getTime() ?? parseTimestamp(b.firstSeenAt)?.getTime() ?? Infinity
        return aTime - bTime
      })

      const targetPerson = selectedPersons[0]
      const sourcePersons = selectedPersons.slice(1)

      console.log('[Merge] After sort - TARGET (keeping):', { id: targetPerson.id, name: targetPerson.name })
      console.log('[Merge] After sort - SOURCES (merging in):', sourcePersons.map(p => ({ id: p.id, name: p.name })))

      const mergeFn = httpsCallable(functions, 'mergePersons')

      // Merge each source into the target sequentially
      for (const source of sourcePersons) {
        console.log(`[Merge] Merging source "${source.name}" (${source.id}) into target "${targetPerson.name}" (${targetPerson.id})`)
        await mergeFn({
          groupId,
          sourcePersonId: source.id,
          targetPersonId: targetPerson.id,
        })
      }

      exitSelectionMode()
      setMergeConfirmOpen(false)
      loadPersons()
    } catch (err) {
      console.error('Failed to merge persons:', err)
      setMergeError(t('merge.mergeFailed'))
    } finally {
      setMerging(false)
    }
  }

  const handleArchiveSelected = async () => {
    if (selectedPersonIds.size < 1) return

    setBulkProcessing(true)
    setMergeError(null)

    try {
      const updateFn = httpsCallable(functions, 'updatePerson')
      const archivedAt = new Date().toISOString()

      for (const personId of selectedPersonIds) {
        await updateFn({ groupId, personId, archivedAt })
      }

      exitSelectionMode()
      setBulkArchiveConfirmOpen(false)
      loadPersons()
    } catch (err) {
      console.error('Failed to archive persons:', err)
      setMergeError(t('persons.bulkArchiveFailed'))
    } finally {
      setBulkProcessing(false)
    }
  }

  const handleDeleteSelected = async () => {
    if (selectedPersonIds.size < 1) return

    setBulkProcessing(true)
    setMergeError(null)

    try {
      const deleteFn = httpsCallable(functions, 'deletePerson')

      for (const personId of selectedPersonIds) {
        await deleteFn({ groupId, personId })
      }

      exitSelectionMode()
      setBulkDeleteConfirmOpen(false)
      loadPersons()
    } catch (err) {
      console.error('Failed to delete persons:', err)
      setMergeError(t('persons.bulkDeleteFailed'))
    } finally {
      setBulkProcessing(false)
    }
  }

  const handlePersonClick = (person: Person) => {
    if (selectionMode) {
      togglePersonSelection(person.id)
      return
    }
    setSelectedPerson(person)
    const params = searchParams.toString()
    navigate(params ? `/persons/${person.id}?${params}` : `/persons/${person.id}`)
  }

  const handlePersonUpdated = useCallback(() => {
    loadPersons()
  }, [loadPersons])

  const handlePersonDeleted = useCallback(() => {
    // If this was the last item on the page, navigate back; otherwise reload
    if (persons.length <= 1 && canGoPrev) {
      goPrev()
    } else {
      loadPersons()
    }
  }, [loadPersons, persons.length, canGoPrev, goPrev])

  // Compute page numbers for pagination UI
  // Always show first 2, last 2, and current ± 2, with ellipsis for gaps
  const pageNumbers = useMemo(() => {
    const pages: (number | 'ellipsis')[] = []
    if (totalPages <= 9) {
      for (let i = 1; i <= totalPages; i++) pages.push(i)
    } else {
      const visible = new Set<number>()
      // Always include first and last
      visible.add(1)
      visible.add(totalPages)
      // Window around current page
      for (let i = currentPage - 2; i <= currentPage + 2; i++) {
        if (i >= 1 && i <= totalPages) visible.add(i)
      }
      const sorted = Array.from(visible).sort((a, b) => a - b)
      for (let i = 0; i < sorted.length; i++) {
        if (i > 0 && sorted[i] - sorted[i - 1] > 1) pages.push('ellipsis')
        pages.push(sorted[i])
      }
    }
    return pages
  }, [currentPage, totalPages])

  if (loading && persons.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <Alert variant="destructive" className="mb-4 text-left">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
          <Button variant="outline" onClick={() => loadPersons()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            {t('common.retry')}
          </Button>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      {selectionMode ? (
        <div className="flex justify-between items-center bg-primary/5 border border-primary/20 rounded-lg p-3">
          <div className="flex items-center gap-3">
            <CheckSquare className="h-5 w-5 text-primary" />
            <span className="font-medium">
              {t('merge.selected', { count: selectedPersonIds.size })}
            </span>
            {mergeError && (
              <span className="text-sm text-[#7a3325]">{mergeError}</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {showArchived ? (
              /* Archive view: bulk delete */
              <Button
                variant="destructive"
                size="sm"
                disabled={selectedPersonIds.size < 1 || bulkProcessing}
                onClick={() => setBulkDeleteConfirmOpen(true)}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                {t('persons.deleteCount', { count: selectedPersonIds.size })}
              </Button>
            ) : (
              /* Normal view: merge + bulk archive */
              <>
                <Button
                  variant="default"
                  size="sm"
                  disabled={selectedPersonIds.size < 2 || merging}
                  onClick={() => setMergeConfirmOpen(true)}
                >
                  <Merge className="h-4 w-4 mr-2" />
                  {t('merge.mergeCount', { count: selectedPersonIds.size })}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-orange-600 border-orange-300 hover:bg-orange-50"
                  disabled={selectedPersonIds.size < 1 || bulkProcessing}
                  onClick={() => setBulkArchiveConfirmOpen(true)}
                >
                  <Archive className="h-4 w-4 mr-2" />
                  {t('persons.archiveCount', { count: selectedPersonIds.size })}
                </Button>
              </>
            )}
            <Button variant="outline" size="sm" onClick={exitSelectionMode}>
              <X className="h-4 w-4 mr-2" />
              {t('common.cancel')}
            </Button>
          </div>
        </div>
      ) : (
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h2 className="font-serif text-[28px] font-medium leading-[1.05] tracking-[-0.005em] text-ink">
              {t('persons.title')}
            </h2>
            <hr className="gold-rule mt-3" />
            <p className="mt-3 text-[13px] leading-[1.55] text-ink-soft">
              {isLegacyPageMode
                ? t('persons.count', { count: totalCount })
                : t('persons.countOnPage', { count: persons.length, defaultValue: '{{count}} persons on this page' })}
              {isLegacyPageMode && totalPages > 1 && ` \u00B7 ${t('persons.pageOf', { current: currentPage, total: totalPages })}`}
              {!isLegacyPageMode && (canGoPrev || canGoNext) && ` \u00B7 ${t('persons.pageNumber', { page: cursorPageIndex, defaultValue: 'Page {{page}}' })}`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {canManagePersons && (
              <Button variant="default" size="sm" onClick={() => setAddPersonOpen(true)}>
                <UserPlus className="h-4 w-4 mr-2" />
                {t('persons.addPerson')}
              </Button>
            )}
            {canManagePersons && persons.length >= 1 && (
              <Button variant="outline" size="sm" onClick={() => setSelectionMode(true)}>
                <CheckSquare className="h-4 w-4 mr-2" />
                {t('persons.select')}
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => loadPersons()} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              {t('common.refresh')}
            </Button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t('persons.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select
          value={selectedClassification}
          onValueChange={setSelectedClassification}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder={t('persons.allClassifications')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('persons.allClassifications')}</SelectItem>
            {classifications.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: c.color }}
                  />
                  {c.displayName}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {isAdmin && (
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox
              checked={showArchived}
              onCheckedChange={(checked) => setShowArchived(!!checked)}
            />
            <Archive className="h-4 w-4 text-muted-foreground" />
            {t('persons.showArchivedExpired')}
          </label>
        )}
      </div>

      {/* Persons grid */}
      {persons.length === 0 && !loading ? (
        <Card>
          <CardContent className="py-12 text-center">
            <div className="text-muted-foreground">
              <p className="text-lg mb-2">{t('persons.noPersons')}</p>
              <p className="text-sm">
                {searchQuery || selectedClassification !== 'all'
                  ? t('persons.noPersonsFilterHint')
                  : t('persons.noPersonsHint')}
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {persons.map((person) => {
            const isSelected = selectedPersonIds.has(person.id)
            const isArchived = !!person.archivedAt
            const isExpired = !!person.expiresAt && new Date(person.expiresAt) < new Date()
            return (
              <Card
                key={person.id}
                className={`overflow-hidden cursor-pointer transition-colors ${
                  selectionMode && isSelected
                    ? 'border-primary ring-2 ring-primary/20'
                    : 'hover:border-primary/50'
                } ${isArchived || isExpired ? 'opacity-60' : ''}`}
                onClick={() => handlePersonClick(person)}
              >
                <div className="aspect-square bg-paper-2 relative">
                  {person.thumbnailUrl ? (
                    <img
                      src={person.thumbnailUrl}
                      alt={person.name || t('common.unknown')}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                      <User className="h-12 w-12" />
                    </div>
                  )}
                  {selectionMode && (
                    <div className="absolute top-2 left-2">
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => togglePersonSelection(person.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="h-5 w-5 bg-[var(--white-frame)]/90 border-2"
                      />
                    </div>
                  )}
                  <Badge
                    className="absolute top-2 right-2"
                    style={{
                      backgroundColor: person.classificationColor || '#6B7280',
                      color: 'white',
                    }}
                  >
                    {person.classificationName || t('common.unknown')}
                  </Badge>
                  {isArchived && (
                    <Badge variant="secondary" className="absolute bottom-2 right-2 bg-orange-100 text-orange-700 border-orange-300">
                      <Archive className="h-3 w-3 mr-1" />
                      {t('personDetail.archived')}
                    </Badge>
                  )}
                  {!isArchived && isExpired && (
                    <Badge variant="secondary" className="absolute bottom-2 right-2 bg-[#f5e3df] text-[#5b2419] border-red-300">
                      {t('personDetail.expired')}
                    </Badge>
                  )}
                </div>
                <CardContent className="p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium truncate">
                      {person.name || t('common.unknown')}
                    </span>
                  </div>

                  {person.notes && (
                    <p className="text-sm text-muted-foreground truncate">
                      {person.notes}
                    </p>
                  )}

                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Eye className="h-3 w-3" />
                      {t('persons.sightings', { count: person.totalSightings })}
                    </div>
                    <div className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {formatRelativeTime(person.lastSeenAt)}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}

      {/* Pagination */}
      {isLegacyPageMode && totalPages > 1 && (
        <div className="flex items-center justify-center gap-1 pt-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(currentPage - 1)}
            disabled={currentPage <= 1 || loading}
          >
            <ChevronLeft className="h-4 w-4" />
            {t('common.previous')}
          </Button>
          {pageNumbers.map((p, i) =>
            p === 'ellipsis' ? (
              <span key={`ellipsis-${i}`} className="px-2 text-muted-foreground">...</span>
            ) : (
              <Button
                key={p}
                variant={p === currentPage ? 'default' : 'outline'}
                size="sm"
                className="min-w-[36px]"
                onClick={() => setPage(p)}
                disabled={loading}
              >
                {p}
              </Button>
            )
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(currentPage + 1)}
            disabled={currentPage >= totalPages || loading}
          >
            {t('common.next')}
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
      {!isLegacyPageMode && (canGoPrev || canGoNext) && (
        <div className="flex items-center justify-center gap-1 pt-4 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={goPrev}
            disabled={!canGoPrev || loading}
          >
            <ChevronLeft className="h-4 w-4" />
            {t('common.previous')}
          </Button>
          {Array.from({ length: Math.min(cursorStack.length, 4) }, (_, i) => {
            const offset = Math.min(cursorStack.length, 4) - i // 4, 3, 2, 1
            const pageNum = cursorPageIndex - offset
            return (
              <Button
                key={`back-${offset}`}
                variant="outline"
                size="sm"
                className="min-w-[36px]"
                onClick={() => goBackPages(offset)}
                disabled={loading}
              >
                {pageNum}
              </Button>
            )
          })}
          <Button
            variant="default"
            size="sm"
            className="min-w-[36px]"
            disabled
          >
            {cursorPageIndex}
          </Button>
          {hasMore && Array.from({ length: 4 }, (_, i) => i + 1).map(offset => (
            <Button
              key={`fwd-${offset}`}
              variant="outline"
              size="sm"
              className="min-w-[36px]"
              onClick={() => offset === 1 ? goNext() : goForwardPages(offset)}
              disabled={loading}
            >
              {cursorPageIndex + offset}
            </Button>
          ))}
          <Button
            variant="outline"
            size="sm"
            onClick={goNext}
            disabled={!canGoNext || loading}
          >
            {t('common.next')}
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Person Detail Dialog */}
      <PersonDetailDialog
        open={detailDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            const params = searchParams.toString()
            navigate(params ? `/persons?${params}` : '/persons')
          }
        }}
        personId={selectedPersonId}
        initialPerson={selectedPerson}
        groupId={groupId}
        onUpdated={handlePersonUpdated}
        onDeleted={handlePersonDeleted}
        onNavigateToSightings={onNavigateToSightings}
        canManagePersons={canManagePersons}
        isAdmin={isAdmin}
        isArchiveView={showArchived}
      />

      {/* Add Person Dialog */}
      <AddPersonDialog
        open={addPersonOpen}
        onOpenChange={setAddPersonOpen}
        groupId={groupId}
        onPersonCreated={() => loadPersons()}
      />

      {/* Merge confirmation dialog */}
      <AlertDialog open={mergeConfirmOpen} onOpenChange={(open) => { if (!merging) setMergeConfirmOpen(open) }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('merge.mergeTitle', { count: selectedPersonIds.size })}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>
                  {t('merge.mergeDescription', { count: selectedPersonIds.size - 1 })}
                </p>
                {(() => {
                  const selected = persons.filter(p => selectedPersonIds.has(p.id))
                  selected.sort((a, b) => {
                    const aTime = parseTimestamp(a.createdAt)?.getTime() ?? parseTimestamp(a.firstSeenAt)?.getTime() ?? Infinity
                    const bTime = parseTimestamp(b.createdAt)?.getTime() ?? parseTimestamp(b.firstSeenAt)?.getTime() ?? Infinity
                    return aTime - bTime
                  })
                  const target = selected[0]
                  const sources = selected.slice(1)
                  return (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 p-2 bg-primary/5 border border-primary/20 rounded">
                        <div className="w-8 h-8 rounded overflow-hidden bg-paper-2 flex-shrink-0">
                          {target?.thumbnailUrl ? (
                            <img src={target.thumbnailUrl} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                              <User className="h-4 w-4" />
                            </div>
                          )}
                        </div>
                        <div className="min-w-0">
                          <span className="font-medium text-sm">{target?.name || t('common.unknown')}</span>
                          <span className="text-xs text-primary ml-2">{t('merge.keeping')}</span>
                        </div>
                      </div>
                      {sources.map(s => (
                        <div key={s.id} className="flex items-center gap-2 p-2 bg-muted rounded">
                          <div className="w-8 h-8 rounded overflow-hidden bg-paper-2 flex-shrink-0">
                            {s.thumbnailUrl ? (
                              <img src={s.thumbnailUrl} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                                <User className="h-4 w-4" />
                              </div>
                            )}
                          </div>
                          <div className="min-w-0">
                            <span className="text-sm">{s.name || t('common.unknown')}</span>
                            <span className="text-xs text-muted-foreground ml-2">{t('merge.willBeRemoved')}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )
                })()}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={merging}>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleMergeSelected} disabled={merging}>
              {merging ? t('merge.merging') : t('common.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk archive confirmation dialog */}
      <AlertDialog open={bulkArchiveConfirmOpen} onOpenChange={(open) => { if (!bulkProcessing) setBulkArchiveConfirmOpen(open) }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('persons.bulkArchiveTitle', { count: selectedPersonIds.size })}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('persons.bulkArchiveDescription', { count: selectedPersonIds.size })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkProcessing}>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleArchiveSelected} disabled={bulkProcessing}>
              {bulkProcessing ? t('common.saving') : t('persons.archiveCount', { count: selectedPersonIds.size })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk delete confirmation dialog */}
      <AlertDialog open={bulkDeleteConfirmOpen} onOpenChange={(open) => { if (!bulkProcessing) setBulkDeleteConfirmOpen(open) }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('persons.bulkDeleteTitle', { count: selectedPersonIds.size })}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('persons.bulkDeleteDescription', { count: selectedPersonIds.size })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkProcessing}>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteSelected}
              disabled={bulkProcessing}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {bulkProcessing ? t('common.deleting') : t('persons.deleteCount', { count: selectedPersonIds.size })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
